"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { LoyaltyPointsIcon } from "@/components/ui/LoyaltyPointsIcon";

/** The figures a result is really about, shown in place of the photo. */
/**
 * Which Gondola earning an earn-stack row is: picks the icon, and matches the
 * rows in the standard earnings module (see suites/DirectBookingSection).
 */
export type EarnSource = "loyalty" | "nights" | "card" | "cash";

export type ResultPanel =
  | {
      kind: "compare";
      left: { label: string; value: string; note?: string };
      right: { label: string; value: string; note?: string };
      /** Which side the agent is recommending. Tinted green, as the saving. */
      winner: "left" | "right";
    }
  | {
      kind: "stack";
      rows: { label: string; value: string; source: EarnSource }[];
      /** Loyalty program whose icon the "loyalty" rows carry. */
      program: string;
      /** `note` rides under the value, e.g. the percentage the total works out to. */
      total: { label: string; value: string; note?: string };
    }
  | {
      kind: "drop";
      /** The stay's dates, so the panel answers a question about them. */
      dates: string;
      was: { label: string; value: string };
      now: { label: string; value: string };
      saved: { label: string; value: string };
    };

export interface Exchange {
  prompt: string;
  status: string;
  /** Property photo. Omit when `panel` carries the result instead. */
  image?: string;
  /** Structured result shown instead of `image`. */
  panel?: ResultPanel;
  result: {
    title: string;
    rateLine: string;
    goldLine: string;
  };
  reply: string;
}

const defaultExchanges: Exchange[] = [
  {
    prompt:
      "I loved the Park Hyatt Mendoza. What's the most similar hotel in Mexico City for October 16 to 18, with a great points redemption or a suite upgrade?",
    status: "checked 212 rates across 31 hotels",
    image: "/assets/landing-page/mcp-demo/mexico-city.webp",
    result: {
      title: "Andaz Mexico City Condesa",
      rateLine: "25,000 pts/night · suites open",
      goldLine: "2.2¢ per point",
    },
    reply:
      "Closest match to the Park Hyatt: Andaz Mexico City Condesa. Suite upgrades were available on both nights.",
  },
  {
    prompt:
      "I want a Hyatt in New York for November 12 to 14. Where does my Bilt balance go furthest? Show me the cents-per-point on each.",
    status: "compared 9 redemptions with your transfer partners",
    image: "/assets/landing-page/mcp-demo/new-york.webp",
    result: {
      title: "Hyatt Grand Central New York",
      rateLine: "17,000 pts/night via Bilt transfer",
      goldLine: "2.4¢ per point, best of 9",
    },
    reply:
      "Transferring 34,000 Bilt points to World of Hyatt beats every cash rate in midtown. Want the transfer steps?",
  },
  {
    prompt:
      "Show me my upcoming trips and tell me where I could save money, earn more points, or rebook at a better rate. Would shifting my dates slightly help?",
    status: "rechecked 5 upcoming stays across 14 date windows",
    image: "/assets/landing-page/mcp-demo/chicago.webp",
    result: {
      title: "Chicago · November 3 to 6",
      rateLine: "same room now $118 less",
      goldLine: "shifting one night saves $67 more",
    },
    reply:
      "Two of your five trips can be rebooked cheaper. I can rebook Chicago now and keep your same room.",
  },
  {
    prompt:
      "Find me a hotel in LA for October 3 to 5 I'd actually like, based on my past stays. Compare cash and points with my balances and status.",
    status: "matched against 18 past stays and your balances",
    image: "/assets/landing-page/mcp-demo/los-angeles.webp",
    result: {
      title: "Andaz West Hollywood",
      rateLine: "$312 member rate or 21,000 pts",
      goldLine: "cash wins: 1.5¢ per point",
    },
    reply:
      "Based on your past stays, you'll love the Andaz West Hollywood. Pay cash here; your points are worth more elsewhere.",
  },
];

// The home page's script, exported under its original name so existing importers
// (the tests derive autopilot timings from it) keep working.
export const exchanges = defaultExchanges;

// Cumulative reveal beats (ms from submit). The bubble reveals synchronously
// on submit; later beats must be strictly increasing — each step's delay is
// the difference to the previous beat. Exported so the test derives its
// fake-timer advances from the same table.
export const BEATS = { bubble: 0, status: 900, result: 2100, reply: 3100 };
const AUTOPILOT_SUBMIT_DELAY = 250;
// How long a fully played exchange holds before the reel advances. Generous on
// purpose: the result panel and the reply are the substance of the demo, and the
// earlier 3.6s was gone before a reader had finished the reply.
const AUTOPILOT_NEXT_DELAY = 7000;
// Manual nav (arrow/dot/swipe) fades the current exchange out fully before
// swapping in the next one's data — matches the `reveal()` transition length
// so nothing shows the next exchange's content until it's actually hidden.
// Exported so the test can advance exactly past it.
export const NAV_FADE_MS = 500;
// Exported (with `exchanges` below) so the test can derive exactly how long
// autopilot's typing phase takes for a given prompt.
export const TYPE_MS_PER_CHAR = 12;

const easeOut = "ease-[cubic-bezier(0.23,1,0.32,1)]";

// Renders the numeric half of a result. Kept next to McpDemo rather than exported as a
// general component: these three shapes exist to answer three specific claims.
/** Gondola's standard earnings icons. Loyalty rows use LoyaltyPointsIcon instead,
 *  so it can carry the program's own colour. */
const EARN_ICONS: Record<Exclude<EarnSource, "loyalty">, string> = {
  nights: "/assets/potential-earnings/status-nights.png",
  card: "/assets/potential-earnings/credit-card.svg",
  cash: "/assets/potential-earnings/gondola-cash.svg",
};

const ResultPanelView: React.FC<{ panel: ResultPanel }> = ({ panel }) => {
  if (panel.kind === "compare") {
    const side = (
      entry: { label: string; value: string; note?: string },
      won: boolean
    ) => (
      <div
        className={cn(
          "flex flex-1 flex-col gap-0.5 rounded-lg border p-3",
          won ? "border-success/50 bg-success/10" : "border-border bg-card"
        )}
      >
        <span className="font-figtree text-xs uppercase tracking-wider text-muted-foreground">
          {entry.label}
        </span>
        <span
          className={cn(
            "font-figtree text-xl font-semibold",
            won ? "text-success" : "text-foreground"
          )}
        >
          {entry.value}
        </span>
        {entry.note ? (
          <span className="font-figtree text-xs text-muted-foreground">
            {entry.note}
          </span>
        ) : null}
      </div>
    );
    return (
      <div className="flex items-stretch gap-2 p-4 pb-0">
        {side(panel.left, panel.winner === "left")}
        {side(panel.right, panel.winner === "right")}
      </div>
    );
  }

  if (panel.kind === "stack") {
    return (
      <div className="flex flex-col gap-2.5 p-4 pb-0">
        {panel.rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between gap-3"
          >
            <span className="flex min-w-0 items-center gap-2.5">
              {row.source === "loyalty" ? (
                <LoyaltyPointsIcon program={panel.program} size={24} />
              ) : (
                <img
                  src={EARN_ICONS[row.source]}
                  alt=""
                  width={24}
                  height={24}
                  className="size-6 shrink-0"
                  loading="lazy"
                  draggable={false}
                />
              )}
              <span className="min-w-0 font-figtree text-xs text-muted-foreground">
                {row.label}
              </span>
            </span>
            {/* Every row reads in the same neutral weight. Tinting one row green
                against three plain ones and a coloured total made the panel look
                like three unrelated colour systems in eight lines. */}
            <span className="shrink-0 font-figtree text-sm font-semibold text-foreground">
              {row.value}
            </span>
          </div>
        ))}
        <div className="mt-0.5 flex items-baseline justify-between gap-3 border-t border-brand-gold/25 pt-2.5">
          <span className="font-figtree text-sm font-medium text-foreground">
            {panel.total.label}
          </span>
          <span className="flex flex-col items-end gap-0.5">
            {/* Flat Gondola Gold. A brand-gold to brand-gold-dark ramp bottoms out
                at 33% lightness and went brown; --gradient-text-gold starts at
                #ffdd57, a yellow that appears nowhere else in the product. */}
            <span className="font-figtree text-lg font-semibold text-brand-gold">
              {panel.total.value}
            </span>
            {panel.total.note ? (
              <span className="font-figtree text-xs text-muted-foreground">
                {panel.total.note}
              </span>
            ) : null}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5 p-4 pb-0">
      <span className="font-figtree text-xs text-muted-foreground">
        {panel.dates}
      </span>
      <div className="flex items-stretch gap-2">
        <div className="flex flex-1 flex-col gap-0.5 rounded-lg border border-border bg-card p-3">
          <span className="font-figtree text-xs uppercase tracking-wider text-muted-foreground">
            {panel.was.label}
          </span>
          <span className="mt-auto font-figtree text-lg font-semibold text-muted-foreground line-through">
            {panel.was.value}
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-0.5 rounded-lg border border-border bg-card p-3">
          <span className="font-figtree text-xs uppercase tracking-wider text-muted-foreground">
            {panel.now.label}
          </span>
          <span className="mt-auto font-figtree text-lg font-semibold text-foreground">
            {panel.now.value}
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-0.5 rounded-lg border border-success/50 bg-success/10 p-3">
          <span className="font-figtree text-xs uppercase tracking-wider text-muted-foreground">
            {panel.saved.label}
          </span>
          <span className="mt-auto font-figtree text-lg font-semibold text-success">
            {panel.saved.value}
          </span>
        </div>
      </div>
    </div>
  );
};

// An interactive, scripted demo of the MCP conversation. The composer holds a
// real prompt with a send button; pressing it (or autopilot, once the widget
// scrolls into view) lifts the prompt into a sent bubble and plays the
// response beats. Arrows, dots, and touch swipe cycle the example prompts;
// any manual interaction disables autopilot. The composer is deliberately
// read-only — this is a scripted demo, not a live chat. A visually hidden
// transcript keeps every exchange in the crawlable HTML, and reduced-motion
// visitors get instant reveals with no autopilot.
type McpDemoProps = {
  /**
   * Where the prompt box sits relative to the conversation. Defaults to "bottom",
   * the chat-app arrangement the home page uses; /mcp puts it on top so each block
   * reads as a search with its results underneath.
   */
  composerPosition?: "top" | "bottom";
  /**
   * Conversation script to play. Defaults to the full four-exchange reel the home page
   * runs; /mcp passes a single exchange per use-case block so each block demonstrates
   * its own claim. Must not be empty.
   */
  script?: Exchange[];
};

const McpDemo: React.FC<McpDemoProps> = ({
  script,
  composerPosition = "bottom",
}) => {
  const exchanges = script?.length ? script : defaultExchanges;
  const [active, setActive] = useState(0);
  // step: 0 composing · 1 bubble sent · 2 status · 3 result · 4 reply (done)
  const [step, setStep] = useState(0);
  const [autopilot, setAutopilot] = useState(true);
  const [inView, setInView] = useState(false);
  // Characters of the current prompt typed into the composer so far, during
  // autopilot's step-0 composing phase.
  const [typedLength, setTypedLength] = useState(0);
  // Exchange index queued by a manual nav action, applied once the current
  // exchange has fully faded out (see NAV_FADE_MS and goTo below).
  const [pendingActive, setPendingActive] = useState<number | null>(null);
  const reducedMotion = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  // Touch-swipe gesture state: pointer id + start x. Mouse pointers are
  // deliberately excluded (a desktop drag to select the prompt text must not
  // switch prompts), and pointercancel/leave clear the anchor so a scroll
  // flick the browser takes over can't leave a stale start point behind.
  const swipeStart = useRef<{ id: number; x: number } | null>(null);

  const exchange = exchanges[active];

  const submit = useCallback(() => {
    if (reducedMotion.current) {
      setStep(4);
      return;
    }
    setStep(1);
  }, []);

  // Manual navigation fades the current exchange out first (dropping step to
  // 0 hides bubble/status/result/reply together), then swaps in the new
  // exchange's data and starts its reveal chain once nothing is left on
  // screen. Swapping `active` immediately used to flash the NEW exchange's
  // text on the OLD (still-visible, mid-fade-out) elements, since only their
  // opacity animates — their text content changes instantly. Reduced motion
  // skips the fade and jumps straight to the full reply.
  const goTo = useCallback(
    (index: number) => {
      setAutopilot(false);
      const target = (index + exchanges.length) % exchanges.length;
      if (reducedMotion.current) {
        setActive(target);
        setStep(4);
        return;
      }
      setStep(0);
      setPendingActive(target);
    },
    [exchanges.length]
  );

  // Apply a queued manual-nav target once the fade-out transition finishes.
  useEffect(() => {
    if (pendingActive === null) {
      return;
    }
    const timer = setTimeout(() => {
      setActive(pendingActive);
      setPendingActive(null);
      setStep(1);
    }, NAV_FADE_MS);
    return () => clearTimeout(timer);
  }, [pendingActive]);

  useEffect(() => {
    reducedMotion.current =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion.current) {
      // Show the first exchange fully played, and never self-drive.
      setAutopilot(false);
      setStep(4);
      return;
    }
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      // No observer available: play rather than sit frozen (mirrors InViewFade).
      setInView(true);
      return;
    }
    // Two-way visibility: autopilot pauses whenever the section scrolls
    // off-screen (a one-shot latch kept the loop, its timers, and its
    // image fetches running for the life of the page).
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: "-120px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Play the response beats once a prompt is sent. Each step schedules only
  // the NEXT step — scheduling every timer at once in a `step`-dependent
  // effect meant the cleanup cleared the later timers the moment the first
  // one advanced the step, freezing the demo after the status line.
  useEffect(() => {
    if (step === 0 || step >= 4) {
      return;
    }
    const delayToNext: Record<number, number> = {
      1: BEATS.status - BEATS.bubble,
      2: BEATS.result - BEATS.status,
      3: BEATS.reply - BEATS.result,
    };
    const timer = setTimeout(
      () => setStep((current) => current + 1),
      delayToNext[step]
    );
    return () => clearTimeout(timer);
  }, [step]);

  // Autopilot: type the prompt into the composer, then submit shortly after,
  // and advance to the next prompt after a played exchange has had time to
  // be read.
  useEffect(() => {
    if (!autopilot || !inView) {
      return;
    }
    if (step === 0) {
      setTypedLength(0);
      const promptLength = exchange.prompt.length;
      let count = 0;
      let submitTimer: ReturnType<typeof setTimeout> | undefined;
      const typeTimer = setInterval(() => {
        count += 1;
        setTypedLength(count);
        if (count >= promptLength) {
          clearInterval(typeTimer);
          submitTimer = setTimeout(submit, AUTOPILOT_SUBMIT_DELAY);
        }
      }, TYPE_MS_PER_CHAR);
      return () => {
        clearInterval(typeTimer);
        clearTimeout(submitTimer);
      };
    }
    if (step === 4) {
      const timer = setTimeout(() => {
        setActive((current) => (current + 1) % exchanges.length);
        setStep(0);
      }, AUTOPILOT_NEXT_DELAY);
      return () => clearTimeout(timer);
    }
  }, [autopilot, inView, step, submit, exchange.prompt, exchanges.length]);

  const reveal = (visible: boolean, extra?: string) =>
    cn(
      "transition-[opacity,transform] duration-500",
      easeOut,
      visible ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0",
      "motion-reduce:translate-y-0 motion-reduce:transition-none",
      extra
    );

  // Conversation stage. Deliberately NOT an aria-live region: the
  // sr-only transcript above already gives assistive tech the full
  // content without depending on animation timing, and a live
  // region here would re-announce every autopilot cycle.
  const conversation = (
    <div className="flex min-h-[380px] flex-col gap-4 p-5 lg:min-h-[420px] lg:p-6">
      {/* Sent prompt bubble */}
      <div
        className={reveal(
          step >= 1,
          "max-w-[90%] self-end rounded-2xl rounded-br-sm bg-muted px-5 py-3.5 font-figtree text-sm leading-relaxed text-foreground"
        )}
        aria-hidden={step < 1}
      >
        {exchange.prompt}
      </div>

      {/* Agent status */}
      <span
        className={reveal(step >= 2, "font-mono text-xs text-muted-foreground")}
        aria-hidden={step < 2}
      >
        {exchange.status}
        <span className={cn(step < 4 && "motion-safe:animate-pulse")}>
          {step < 4 ? "..." : ""}
        </span>
      </span>

      {/* Result card */}
      <div
        className={reveal(
          step >= 3,
          "overflow-hidden rounded-xl border border-border bg-background"
        )}
        aria-hidden={step < 3}
      >
        {exchange.panel ? (
          <ResultPanelView panel={exchange.panel} />
        ) : exchange.image ? (
          <img
            src={exchange.image}
            alt=""
            className="h-24 w-full object-cover lg:h-28"
            width={1280}
            height={256}
            loading="lazy"
            draggable={false}
          />
        ) : null}
        <div className="flex flex-col gap-0.5 p-4">
          <span className="font-figtree text-base font-semibold text-foreground">
            {exchange.result.title}
          </span>
          <span className="font-figtree text-sm text-muted-foreground">
            {exchange.result.rateLine}
          </span>
          <span className="font-figtree text-sm font-semibold text-brand-gold">
            {exchange.result.goldLine}
          </span>
        </div>
      </div>

      {/* Agent reply */}
      <p
        className={reveal(
          step >= 4,
          "border-l-2 border-brand-gold/40 pl-4 font-figtree text-sm leading-relaxed text-foreground"
        )}
        aria-hidden={step < 4}
      >
        {exchange.reply}
      </p>
    </div>
  );

  // Composer
  const composer = (
    <div
      className={cn(
        "p-4",
        composerPosition === "top"
          ? "border-b border-border"
          : "border-t border-border"
      )}
    >
      <div className="flex items-center gap-3 rounded-lg border border-input bg-muted px-4 py-3">
        <span
          className={cn(
            "relative min-h-[1.25rem] flex-1 font-figtree text-sm leading-relaxed",
            step === 0 ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {/* Invisible sizer holding the whole prompt, so the box is already at its
              final height before the first character lands: the prompt types from the
              top left and nothing below it moves. A fixed two-line window with the
              text anchored to the bottom kept the box input-sized on a phone, but it
              made the prompt climb as it typed, which read worse than a tall box. */}
          <span aria-hidden="true" className="invisible block">
            {exchange.prompt}
          </span>
          <span
            data-testid="mcp-demo-composer-text"
            className="absolute inset-0 block"
          >
            {step === 0
              ? exchange.prompt.slice(0, typedLength)
              : "Ask about your next trip"}
            {step === 0 && typedLength < exchange.prompt.length && (
              <span
                aria-hidden="true"
                className="ml-0.5 inline-block h-[1em] w-px animate-pulse bg-foreground align-middle"
              />
            )}
          </span>
        </span>
        <Button
          size="none"
          variant="link"
          aria-label="Send prompt"
          // aria-disabled + no-op guard instead of `disabled`: a truly
          // disabled button drops keyboard focus to <body> the moment
          // it is activated (it disables itself synchronously).
          aria-disabled={step !== 0}
          onClick={() => {
            if (step !== 0) {
              return;
            }
            setAutopilot(false);
            submit();
          }}
          className={cn(
            "h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-brand-gold to-brand-gold-dark text-pure-white no-underline transition-transform hover:opacity-90 active:scale-95",
            step !== 0 && "opacity-40"
          )}
        >
          <ArrowUp className="size-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div ref={rootRef} className="flex w-full flex-col gap-4">
      {/* Hidden full transcript for crawlers and assistive tech */}
      <ul className="sr-only">
        {exchanges.map((entry) => (
          <li key={entry.prompt}>
            <p>{entry.prompt}</p>
            <p>{entry.result.title}</p>
            <p>{entry.result.rateLine}</p>
            <p>{entry.result.goldLine}</p>
            <p>{entry.reply}</p>
          </li>
        ))}
      </ul>

      {/* Demo frame — gradient hairline edge over a soft gold glow, with
          minimal app-window chrome so it reads as a real surface */}
      <div className="relative isolate">
        <div
          aria-hidden
          className="absolute -inset-6 -z-10 rounded-full bg-brand-gold/10 blur-3xl"
        />
        <div className="rounded-2xl bg-gradient-to-b from-brand-gold/25 via-border to-border p-px">
          <div
            className="overflow-hidden rounded-[15px] bg-card"
            onPointerDown={(event) => {
              if (
                exchanges.length < 2 ||
                event.pointerType === "mouse" ||
                !event.isPrimary
              ) {
                return;
              }
              swipeStart.current = { id: event.pointerId, x: event.clientX };
            }}
            onPointerUp={(event) => {
              if (swipeStart.current?.id !== event.pointerId) {
                return;
              }
              const delta = event.clientX - swipeStart.current.x;
              swipeStart.current = null;
              if (Math.abs(delta) > 48) {
                goTo(active + (delta < 0 ? 1 : -1));
              }
            }}
            onPointerCancel={() => {
              swipeStart.current = null;
            }}
            onPointerLeave={() => {
              swipeStart.current = null;
            }}
          >
            {/* Window title bar */}
            <div className="relative flex items-center border-b border-border px-4 py-3">
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
                <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
                <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
              </div>
              <span className="absolute inset-x-0 text-center font-mono text-xs tracking-wider text-muted-foreground">
                gondola mcp
              </span>
            </div>
            {composerPosition === "top" ? composer : null}
            {conversation}
            {composerPosition === "bottom" ? composer : null}
          </div>
        </div>
      </div>

      {/* Prompt switcher, only meaningful with more than one exchange to move between */}
      <div
        className={cn(
          "flex items-center justify-center gap-4",
          exchanges.length < 2 && "hidden"
        )}
      >
        <Button
          size="none"
          variant="ghost"
          aria-label="Previous example"
          onClick={() => goTo(active - 1)}
          className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-4" />
        </Button>
        <div className="flex items-center gap-1.5">
          {exchanges.map((entry, index) => (
            <Button
              key={entry.prompt}
              size="none"
              variant="ghost"
              aria-label={`Show example ${index + 1}`}
              aria-current={index === active ? "true" : undefined}
              onClick={() => goTo(index)}
              // A 24px hit target wrapping the 6px visual dot.
              className="group/dot flex h-6 w-6 items-center justify-center rounded-full p-0"
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full transition-colors duration-300",
                  index === active
                    ? "bg-brand-gold"
                    : "bg-muted-foreground/30 group-hover/dot:bg-muted-foreground/50"
                )}
              />
            </Button>
          ))}
        </div>
        <Button
          size="none"
          variant="ghost"
          aria-label="Next example"
          onClick={() => goTo(active + 1)}
          className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground"
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
};

export default McpDemo;
